"use client";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import dynamic from "next/dynamic";

import {
  handleMessageModalOpened,
  handleShareModalOpened,
  setListingId,
  handleReportModalOpened,
  setOdaPortalBoardUrl,
} from "@/src/store/listingDetail";
import { handleFavoriteModalOpened, handleListingFavorited } from "@/src/store/favorite";

import { favoriteHelpers } from "@/src/helpers/favorite";
import { Ga4WithEventParameters } from "@/src/helpers/google-tag-manager/events/ga4-with-eventparameters";
import { normalizeTurkishText } from "@/src/helpers/normalize-turkish-text";
import { sendVlProductDetailEvent } from "@/src/helpers/google-tag-manager/events/vl-product-detail-event";

import StickyHeader from "components/listing-detail/sticky-header";
import Container from "components/grid/container";
import PageTitle from "components/listing-detail/page-title";
import Breadcrumb from "components/breadcrumb";
import PhotoThumbnails from "components/listing-detail/photo-thumbnails";
import ContentWrapper from "components/listing-detail/content-wrapper";
import DetailInfoScreen from "components/common/detail-info-screen";
import ModalSendMessage from "components/modal-send-message";
import ModalShare from "components/listing-detail/modal-share";
import ModalFavorite from "components/listing-detail/modal-favorite";
import ModalReportIncorrect from "components/listing-detail/modal-report-incorrect";
import StickyInfoBarWrapper from "components/listing-detail/sticky-info-bar-wrapper";
import GlobalGa4GtmSearchIdEvent from "components/gtm-load-events/globalGa4GtmSearchIdEvent";
import { setViewDetailEvent, getSimilarListingsOrRecommended } from "@/src/services/listingDetail";
import NearbyListings from "components/listing-detail/nearby-listings";

import EidsVerifiedLogo from "assets/svg/eids-verified.svg";

import styles from "./styles.module.css";

const Modal = dynamic(() => import("components/common/modal"), { ssr: false });
const SuggestedListings = dynamic(() => import("components/listing-detail/suggested-listings"), {
  ssr: false,
  suspense: false,
});

const Wrapper = ({ pageResponse }) => {
  const {
    detailData: {
      title,
      imagesFullPath,
      url,
      id,
      fromOwner,
      images,
      videoUrl,
      virtualTourUrl,
      location: {
        city: { name: cityName },
        district: { name: districtName },
        town: { name: townName },
        locality: { name: localityName },
      },
      decorator,
      properties,
      priceDetail,
    },
    nearbyListings: { townUrlList },
    similarRealEstateList,
    listingPriceHistory,
  } = pageResponse;

  const dispatch = useDispatch();
  const getAllFavoritesByAccount = favoriteHelpers();

  const messageModalReduxState = useSelector((state) => state.listingDetail.messageModalOpened);
  const shareModalReduxState = useSelector((state) => state.listingDetail.shareModalOpened);
  const favoriteModalReduxState = useSelector((state) => state.favorite.favoriteModalOpened);
  const listingIdReduxState = useSelector((state) => state.listingDetail.listingId);
  const incorrectModalReduxState = useSelector((state) => state.listingDetail.reportListingModalOpened);
  const isInfoModalRendered = useSelector((state) => state.listingDetail.detailInfoModalOpened);
  const boardUrlReduxState = useSelector((state) => state.listingDetail.odaPortalBoardUrl);

  const { boardUrl } = decorator;

  if (boardUrl && !boardUrlReduxState) dispatch(setOdaPortalBoardUrl(boardUrl));

  if (!listingIdReduxState) dispatch(setListingId(id));

  const [suggestedListingsData, setSuggestedListingsData] = useState(null);

  const handleModalFavoriteSuccessCallback = async () => {
    Ga4WithEventParameters({
      eventName: "favorite",
      eventParameters: {
        item_id: id.toString(),
        item_type: "ilan",
        city: normalizeTurkishText(cityName),
        town: normalizeTurkishText(districtName),
        neighborhood: normalizeTurkishText(townName),
        district: normalizeTurkishText(localityName) || undefined,
      },
    });
    dispatch(handleFavoriteModalOpened());
    dispatch(handleListingFavorited(true));
    await getAllFavoritesByAccount();
  };

  const setListingViewDetailEvent = async () => await setViewDetailEvent({ listingId: id });

  const isEidsVerifiedValue = pageResponse?.detailData?.eidsRealEstate?.isEidsVerified;

  useEffect(() => {
    if (isInfoModalRendered) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style = "";
    }
  }, [isInfoModalRendered]);

  useEffect(() => {
    setListingViewDetailEvent();

    sendVlProductDetailEvent({
      title,
      id,
      price: priceDetail?.price?.toString(),
      cityName,
      districtName,
      townName,
      properties,
    });
  }, []);

  const fetchSuggestedListings = async (id, setData) => {
    try {
      const response = await getSimilarListingsOrRecommended({
        listingId: id,
        similarLimit: 3,
        recommendationLimit: 10,
      });
      setData(response.data);
    } catch (error) {
      setData(null);
    }
  };

  useEffect(() => {
    fetchSuggestedListings(id, setSuggestedListingsData);
  }, []);

  return (
    <>
      <StickyHeader data={pageResponse.detailData} />
      <Container>
        <div className={styles.wrapperHeaderContainer}>
          <div className={styles.wrapperSummaryContainer}>
            <Breadcrumb data={pageResponse?.detailData?.breadcrumbs} />
            <PageTitle>{title}</PageTitle>
          </div>
          {isEidsVerifiedValue && (
            <div className={styles.eidsLogoContainer}>
              <EidsVerifiedLogo />
            </div>
          )}
        </div>
        <PhotoThumbnails listingId={id} images={images} videoUrl={videoUrl} virtualTourUrl={virtualTourUrl} />
        <ContentWrapper
          data={pageResponse.detailData}
          similarRealEstateList={similarRealEstateList}
          listingPriceHistory={listingPriceHistory}
        />
        {suggestedListingsData && typeof window !== "undefined" && <SuggestedListings data={suggestedListingsData} />}
        {townName && townUrlList?.length > 0 && <NearbyListings townName={townName} townUrlList={townUrlList} />}
      </Container>
      <Modal isOpen={messageModalReduxState} handleClose={() => dispatch(handleMessageModalOpened())}>
        <ModalSendMessage data={pageResponse.detailData} />
      </Modal>
      <Modal isOpen={shareModalReduxState} handleClose={() => dispatch(handleShareModalOpened())}>
        <ModalShare
          media={imagesFullPath.length ? imagesFullPath[0] : null}
          title={title}
          url={url}
          listingId={listingIdReduxState}
        />
      </Modal>
      <Modal isOpen={favoriteModalReduxState} handleClose={() => dispatch(handleFavoriteModalOpened())}>
        <ModalFavorite listingId={listingIdReduxState} successCallback={handleModalFavoriteSuccessCallback} />
      </Modal>
      <Modal isOpen={incorrectModalReduxState} handleClose={() => dispatch(handleReportModalOpened())}>
        <ModalReportIncorrect isPersonalListing={fromOwner} />
      </Modal>
      <StickyInfoBarWrapper />
      {isInfoModalRendered && <DetailInfoScreen data={pageResponse.detailData} />}
      <GlobalGa4GtmSearchIdEvent pageName="listingdetail" />
    </>
  );
};

Wrapper.propTypes = {
  pageResponse: PropTypes.object,
};

export default Wrapper;
